/**
 * Level schema (zod) — the data-driven contract for a stage (DATA_SCHEMAS §5–6).
 * Geometry + path + typed slots + a wave script + rewards + star objectives +
 * an N-tier difficulty ladder. Validating on load means bad content fails fast
 * with a clear error instead of silently breaking the game.
 *
 * `m1Spawn` is the M1 placeholder spawner; it stays until the WaveSystem (M2
 * step 6) consumes `waves[]`, then it's retired.
 */
import { z } from 'zod';
import { SlotTypeSchema, Vec2Schema } from './common';
import { generatePathFromGeometry } from '@/battle/geometry';
import type { Vec2 } from '@/battle/Path';

export { SlotTypeSchema, Vec2Schema };
export type { SlotType } from './common';

export const SlotSchema = z.object({
  id: z.string(),
  type: SlotTypeSchema,
  x: z.number(),
  y: z.number(),
  /** Slot opens at/after this wave index (0 = available from the start). */
  lockedUntilWave: z.number().int().nonnegative().default(0),
});

export const PlatformSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  /** Optional explicit walk order; defaults to array index. */
  order: z.number().int().nonnegative().optional(),
});

/**
 * A stairwell joins two platforms into the walk path: enemies descend it from
 * platform `from`'s exit to platform `to`'s entry. `from`/`to` index into
 * `platforms[]`. `steps` is render-only (how many discrete steps to draw). The
 * indexed model leaves room for branching stages later (a platform can feed
 * more than one stairwell) without committing to it now.
 */
export const StairwellSchema = z.object({
  from: z.number().int().nonnegative(),
  to: z.number().int().nonnegative(),
  steps: z.number().int().positive().default(6),
});

/** One concurrent spawn group within a wave (DATA_SCHEMAS §6). */
export const WaveGroupSchema = z.object({
  enemyId: z.string(),
  count: z.number().int().positive(),
  spacingS: z.number().nonnegative(),
  startDelayS: z.number().nonnegative().default(0),
  /** Spawn mouth index (0/1) for `converging` stages. */
  atMouth: z.number().int().nonnegative().default(0),
  /** Which walk path (lane) this group's enemies follow on a BRANCHING stage.
   *  Indexes into the level's `paths[]`; 0 = the first/main lane (the default,
   *  and the only lane on a single-path stage). Branching stages (e.g. stage 2's
   *  twin cascades) split a wave across lanes 0 and 1 so the two streams must be
   *  managed separately — slow one (Frost) or the reconvergence doubles up. */
  lane: z.number().int().nonnegative().default(0),
});

export const WaveSchema = z.object({
  index: z.number().int().nonnegative(),
  /** Bonus gold on wave clear. */
  rewardGold: z.number().nonnegative().default(0),
  /** Gold/sec saved that is paid out if the wave is called early. */
  callEarlyBonusPerS: z.number().nonnegative().default(0),
  isBoss: z.boolean().default(false),
  groups: z.array(WaveGroupSchema).min(1),
});

/**
 * One difficulty tier. Generalised to an N-tier ladder (not just
 * normal/hard/heroic): campaign tiers + post-game tiers. `tier` is the integer
 * the Progression Score floors to (DATA_SCHEMAS §8 progression model).
 */
export const DifficultySchema = z.object({
  tier: z.number().int().positive(),
  name: z.string(),
  enemyHp: z.number().positive().default(1),
  enemyDmg: z.number().positive().default(1),
  rewardMult: z.number().positive().default(1),
  /** True once this difficulty crosses into post-game (meta becomes expected). */
  postGame: z.boolean().default(false),
});

export const StarObjectivesSchema = z.object({
  /** ★★: keep at least this fraction of starting lives. */
  two: z.object({ minLivesPct: z.number().min(0).max(1) }).optional(),
  /** ★★★: lose at most this many lives (0 = flawless). */
  three: z.object({ maxLivesLost: z.number().int().nonnegative() }).optional(),
});

export const RewardsSchema = z.object({
  /** Favor (per-realm meta currency) — Crowns alias during transition. */
  favor: z.number().nonnegative().default(0),
  crowns: z.number().nonnegative().default(0),
  firstClearBonus: z.number().nonnegative().default(0),
  loot: z
    .array(z.object({ tableId: z.string(), chance: z.number().min(0).max(1) }))
    .default([]),
});

/** Placeholder M1 spawner; retired once WaveSystem consumes `waves[]`. */
export const M1SpawnSchema = z.object({
  count: z.number().int().positive(),
  intervalS: z.number().positive(),
  enemyHp: z.number().positive(),
  enemySpeed: z.number().positive(),
});

export const LevelSchema = z.object({
  id: z.string(),
  name: z.string(),
  biome: z.string().default('forest'),
  /** The Mythic Pantheon realm this stage belongs to (e.g. "olympus"). */
  realm: z.string().default('olympus'),
  direction: z
    .enum(['topToBottom', 'bottomToTop', 'converging'])
    .default('topToBottom'),
  world: z.object({ width: z.number().positive(), height: z.number().positive() }),
  startingGold: z.number().nonnegative().default(200),
  startingLives: z.number().positive().default(20),
  platforms: z.array(PlatformSchema),
  stairwells: z.array(StairwellSchema).default([]),
  /** Enemy walk path. Optional in source: if omitted it is *generated* from
   *  platforms + stairwells at load (see `parseLevel`). An explicit path always
   *  wins, so simple test fixtures can still author waypoints directly. */
  path: z.array(Vec2Schema).min(2).optional(),
  /** Multiple walk paths (lanes) for a BRANCHING stage — each a full spawn→exit
   *  polyline. Lanes share waypoints where they run together (so enemies visually
   *  merge) and diverge where they fork. Authored explicitly because the
   *  serpentine generator only handles a single linear chain. When present this
   *  wins over `path`/generation; `path` (singular, downstream) aliases lane 0. */
  paths: z.array(z.array(Vec2Schema).min(2)).min(1).optional(),
  slots: z.array(SlotSchema),
  /** Unit ids the player may deploy on this stage. Omitted = the full roster.
   *  Early stages list a small set so each new stage can introduce a new tool
   *  (e.g. stage 1 teaches block + ranged + hero; mage/frost arrive later). */
  availableUnits: z.array(z.string()).optional(),
  waves: z.array(WaveSchema).default([]),
  rewards: RewardsSchema.optional(),
  starObjectives: StarObjectivesSchema.optional(),
  difficulties: z.array(DifficultySchema).default([]),
  /** Retired M1 placeholder spawner — optional now that WaveSystem drives spawns. */
  m1Spawn: M1SpawnSchema.optional(),
});

/** `path`/`paths` are always populated after `parseLevel` (generated when
 *  omitted), so downstream consumers see guaranteed arrays rather than optionals.
 *  `paths` always has >= 1 lane; `path` aliases `paths[0]`. */
export type Level = Omit<z.infer<typeof LevelSchema>, 'path' | 'paths'> & {
  path: Vec2[];
  paths: Vec2[][];
};
export type Slot = z.infer<typeof SlotSchema>;
export type Wave = z.infer<typeof WaveSchema>;
export type Difficulty = z.infer<typeof DifficultySchema>;

/** Parse + validate raw level JSON, throwing a descriptive error on bad data.
 *  When no `path` is authored, derive the serpentine walk path from the stage's
 *  platforms + stairwells so the lane always rides the surfaces. */
export function parseLevel(raw: unknown): Level {
  const result = LevelSchema.safeParse(raw);
  if (!result.success) {
    throw new Error(`Invalid level data:\n${result.error.toString()}`);
  }
  const data = result.data;
  // Lane resolution order: explicit `paths` (branching) > explicit `path` >
  // generated-from-geometry. `path` (singular) always aliases lane 0 so all the
  // single-path consumers keep working unchanged.
  const paths: Vec2[][] =
    data.paths ?? [data.path ?? generatePathFromGeometry(data)];
  for (const [i, p] of paths.entries()) {
    if (p.length < 2) {
      throw new Error(`Level ${data.id}: walk lane ${i} needs >= 2 waypoints`);
    }
  }
  return { ...data, path: paths[0], paths };
}
